import React, { useContext, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { RoadMaintenance, RoadPavement } from '../../../../../../services/types/Calculator';
import { Modal } from '../../../../../../shared/components';
import { handleKeyDown } from '../../Utils/KeyboardHandlers';
import BasicRadioButton from '../../../../../../shared/components/BasicRadioButton';
import CalculatorSectorContext from '../UrbanInfrastructureTool/CalculatorSectorContext';
import { calculateRoadMaintenance, calculateRoadPavement } from '../../Utils/moduleCalculates';
import RoadPavementDetails from './Details/RoadPavementDetails';

import CalculatorTableWrapper from '../Shared/CalculatorTableWrapper';

interface Props {
  roadPavement: RoadPavement;
  onUpdateRoadPavement: (updatedRoadPavement: RoadPavement) => void;
  roadMaintenance: RoadMaintenance;
  onUpdateRoadMaintenance: (updatedRoadMaintenance: RoadMaintenance) => void;
}

const RoadPavementSection: React.FC<Props> = ({
  roadPavement,
  onUpdateRoadPavement,
  roadMaintenance,
  onUpdateRoadMaintenance,
}) => {
  const { t } = useTranslation();

  const [showDetailsModal, setShowDetailsModal] = useState(false);
  const [totalCarbonSum, setTotalCarbonSum] = useState<number | null>(null);

  const initialRadio =
    roadPavement.isTotalAreaKnown !== null ? Number(roadPavement.isTotalAreaKnown) : null;
  const [radioSelected, setRadioSelected] = useState<number | null>(initialRadio);
  const { measurementSystem } = useContext(CalculatorSectorContext);

  const sqFeetToSqMeters = 0.09290304;
  const [expandedRowIndex, setExpandedRowIndex] = useState<number | null>(initialRadio);

  const handleRadioChange = (value: number) => {
    setRadioSelected(value);

    onUpdateRoadPavement({
      ...roadPavement,
      isTotalAreaKnown: value === 1,
      totalArea: null,
      pavedRoadLength: null,
      numberOfLanes: null,
      moduleId: roadPavement.moduleId,
      subTotal: null,
    });

    setTotalCarbonSum(null);

    setExpandedRowIndex(value);
  };

  const getTextMeasure = (): string => (measurementSystem.id === 1 ? 'Meters' : 'Feet');

  const handleInputTotalArea = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { value } = e.target;

    if (value.length > 10) {
      return;
    }

    onUpdateRoadPavement({
      ...roadPavement,
      totalArea: value === '' ? null : Number(value),
    });
  };

  const handleInputPavedRoad = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { value } = e.target;

    if (value.length > 10) {
      return;
    }

    onUpdateRoadPavement({
      ...roadPavement,
      pavedRoadLength: value === '' ? null : Number(value),
    });
  };

  const handleInputNumberLanes = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { value } = e.target;

    if (value.length > 10) {
      return;
    }

    onUpdateRoadPavement({
      ...roadPavement,
      numberOfLanes: value === '' ? null : Number(value),
    });
  };

  useEffect(() => {
    const areaKnown = roadPavement.isTotalAreaKnown;
    const totalArea = Number(roadPavement.totalArea);

    const roadLength = Number(roadPavement.pavedRoadLength);
    const numberLanes = Number(roadPavement.numberOfLanes);

    if (areaKnown === null) {
      setTotalCarbonSum(null);
      onUpdateRoadPavement({
        ...roadPavement,
        isTotalAreaKnown: null,
        totalArea: null,
        pavedRoadLength: null,
        numberOfLanes: null,
        moduleId: roadPavement.moduleId,
        subTotal: null,
      });

      onUpdateRoadMaintenance({
        ...roadMaintenance,
        areaFromPavement: null,
      });
    } else if (areaKnown && totalArea && roadPavement.totalArea) {
      const factor =
        measurementSystem.id === 2
          ? roadPavement.factorValue
          : roadPavement.factorValue / roadPavement.sqFeetToSqMeters;

      const result = calculateRoadPavement(factor, totalArea);

      const totalAreaforMaint =
        measurementSystem.id === 1 ? totalArea : totalArea * sqFeetToSqMeters;

      onUpdateRoadPavement({
        ...roadPavement,
        totalArea,
        moduleId: 0,
        subTotal: result,
      });
      onUpdateRoadMaintenance({
        ...roadMaintenance,
        areaFromPavement: parseFloat(totalAreaforMaint.toFixed(2)),
      });

      if (roadMaintenance.isCustomArea !== true) {
        const resultMaintenance = calculateRoadMaintenance(
          roadMaintenance.factorValue,
          parseFloat(totalAreaforMaint.toFixed(2))
        );
        onUpdateRoadMaintenance({
          ...roadMaintenance,
          isCustomArea: false,
          subTotal: resultMaintenance,
          areaFromPavement: parseFloat(totalAreaforMaint.toFixed(2)),
        });
      }

      setTotalCarbonSum(result);
    } else if (
      areaKnown === false &&
      roadLength &&
      roadPavement.pavedRoadLength &&
      numberLanes &&
      roadPavement.numberOfLanes
    ) {
      // meters
      const factor = roadPavement.factorValue / roadPavement.sqFeetToSqMeters;
      const resultingRoadlen = numberLanes * roadPavement.factorWidth;
      const pavedRoad = roadLength * 1000;
      const areaCalculated = resultingRoadlen * pavedRoad;

      const result = calculateRoadPavement(factor, areaCalculated);

      onUpdateRoadPavement({
        ...roadPavement,
        totalArea: null,
        pavedRoadLength: roadLength,
        numberOfLanes: numberLanes,
        moduleId: 0,
        subTotal: result,
      });

      onUpdateRoadMaintenance({
        ...roadMaintenance,
        areaFromPavement: parseFloat(areaCalculated.toFixed(2)),
      });

      if (roadMaintenance.isCustomArea !== true) {
        const resultMaintenance = calculateRoadMaintenance(
          roadMaintenance.factorValue,
          parseFloat(areaCalculated.toFixed(2))
        );
        onUpdateRoadMaintenance({
          ...roadMaintenance,
          isCustomArea: false,
          subTotal: resultMaintenance,
          areaFromPavement: parseFloat(areaCalculated.toFixed(2)),
        });
      }

      setTotalCarbonSum(result);
    } else {
      setTotalCarbonSum(null);
      onUpdateRoadPavement({
        ...roadPavement,
        totalArea: null,
        pavedRoadLength: roadPavement.pavedRoadLength,
        numberOfLanes: roadPavement.numberOfLanes,
        moduleId: roadPavement.moduleId,
        subTotal: null,
      });

      onUpdateRoadMaintenance({
        ...roadMaintenance,
        areaFromPavement: null,
      });

      if (roadMaintenance.isCustomArea !== true) {
        onUpdateRoadMaintenance({
          ...roadMaintenance,
          isCustomArea: false,
          subTotal: null,
        });
      }
    }
  }, [
    roadPavement.isTotalAreaKnown,
    roadPavement.totalArea,
    roadPavement.pavedRoadLength,
    roadPavement.numberOfLanes,
  ]);

  return (
    <>
      <div className="row calculator-title-row">
        <div className="radio-calculator">
          <BasicRadioButton
            id="rb_road"
            name="roadspaved_area"
            label="Enter the total area of roads paved, if known."
            radioValue={1}
            active={radioSelected === 1}
            onChange={(value) => handleRadioChange(value)}
          />
          <BasicRadioButton
            id="rb_road"
            name="roadspaved_calculated"
            label="If the total area of roads paved in the project is not known, enter the length of all roads paved and the number of lanes paved. "
            radioValue={0}
            active={radioSelected === 0}
            onChange={(value) => handleRadioChange(value)}
          />
        </div>
      </div>
      <br />
      {expandedRowIndex === 1 && (
        <div className="row calculator-input-row">
          <div className="col-12 col-sm-5 col-md-3 col-lg-2">
            <label className="col-form-label mr-2" htmlFor="sector">
              {t('Square ') + getTextMeasure()}
            </label>
          </div>
          <div className="col-12 col-sm-5 col-md-4 col-lg-2">
            <input
              title="Square"
              type="number"
              id="totalAreaInput"
              value={roadPavement.totalArea !== null ? roadPavement.totalArea : ''}
              onChange={handleInputTotalArea}
              min="0"
              max={99999999.99}
              maxLength={10}
              onKeyDown={handleKeyDown}
              className="calculator-input"
            />
          </div>
        </div>
      )}

      {expandedRowIndex === 0 && (
        <div className="row calculator-input-row">
          <div className="col-12 col-sm-5 col-md-3 col-lg-2">
            <label className="col-form-label mr-2" htmlFor="sector">
              {t('Paved Road Length (Kilometers)')}
            </label>
          </div>
          <div className="col-12 col-sm-5 col-md-4 col-lg-2">
            <input
              title="Paved Road Length (Kilometers)"
              type="number"
              id="roadLengthInput"
              value={roadPavement.pavedRoadLength !== null ? roadPavement.pavedRoadLength : ''}
              onChange={handleInputPavedRoad}
              min="0"
              max={99999999.99}
              maxLength={10}
              onKeyDown={handleKeyDown}
              className="calculator-input"
            />
          </div>

          <div className="col-12 col-sm-5 col-md-3 col-lg-2">
            <label className="col-form-label mr-2" htmlFor="sector">
              {t('Number of Lanes')}
            </label>
          </div>
          <div className="col-12 col-sm-5 col-md-4 col-lg-2">
            <input
              title="Number of Lanes"
              type="number"
              id="numberLanesInput"
              value={roadPavement.numberOfLanes !== null ? roadPavement.numberOfLanes : ''}
              onChange={handleInputNumberLanes}
              min="0"
              max={99999999.99}
              maxLength={10}
              onKeyDown={handleKeyDown}
              className="calculator-input"
            />
          </div>
        </div>
      )}

      <CalculatorTableWrapper
        totalCarbonSum={totalCarbonSum}
        onShowDetailsModal={() => setShowDetailsModal(true)}
        subtotalColSpan={3}
        colGroup={
          <colgroup>
            <col style={{ width: '40%' }} />
            <col style={{ width: '25%' }} />
            <col style={{ width: '25%' }} />
            <col style={{ width: '10%' }} />
          </colgroup>
        }
      />
      <Modal
        title={roadPavement.name}
        visible={showDetailsModal}
        setVisible={() => setShowDetailsModal(false)}
        actions={[{ text: 'Close', action: () => setShowDetailsModal(false) }]}
        customClass="extra_wide-modal"
        onClosed={() => setShowDetailsModal(false)}
      >
        <div className="mb-0 table-responsive">
          <RoadPavementDetails roadPavement={roadPavement} />
        </div>
      </Modal>
    </>
  );
};

export default RoadPavementSection;
